/**
 * HudData — pure HUD data model and formatting helpers (no Phaser).
 *
 * Keeps rendering logic in HudScene; this module is node-testable.
 */

/** Phaser registry key used to share HUD state between StageScene and HudScene. */
export const HUD_REGISTRY_KEY = 'hudPayload';

/** Per-player snapshot written by StageScene each frame. */
export interface PlayerHudData {
  hpFrac: number;
  greenHpFrac: number;
  stars: number;
  score: number;
  lives: number;
  heroColor: number;
  heroName: string;
  /** Active combo hit count (0 = no combo in progress). */
  styleCombo?: number;
  /** Current style grade letter (D/C/B/A/S). */
  styleGrade?: string;
  /** 0..1 backup meter fill fraction. */
  backupFrac?: number;
}

/** Full HUD state committed to the Phaser registry each frame. */
export interface HudPayload {
  p1: PlayerHudData;
  /** null when not in co-op mode. */
  p2: PlayerHudData | null;
  stageName: string;
  /** 1-based wave number. 0 = no wave system active yet. */
  wave: number;
  /** 0..1 boss HP fraction. 0 = no boss present. */
  bossHpFrac: number;
  /** 0..1 elite HP fraction. 0 = no active elite / bar hidden. */
  eliteHpFrac: number;
  /** Display name of the active elite; empty string when none. */
  eliteName: string;
  /** True for exactly one frame when a 1-up is awarded; HudScene flashes the lives display. */
  extraLifeFlash: boolean;
  /** Score Attack score multiplier for the current stage (1 = no banner shown). */
  scoreMultiplier: number;
}

/**
 * Compute elite bar display state from raw payload values.
 *
 * Returns `visible: false` and an empty name when `eliteHpFrac` is 0,
 * matching the boss-bar convention where 0 == "no target present".
 */
export function eliteBarState(
  eliteHpFrac: number,
  eliteName: string,
): { visible: boolean; hpFrac: number; name: string } {
  const visible = eliteHpFrac > 0;
  return {
    visible,
    hpFrac: Math.max(0, Math.min(1, eliteHpFrac)),
    name: visible ? eliteName : '',
  };
}

/** Format a score as an 8-digit zero-padded string (SoR style). */
export function formatScore(score: number): string {
  return Math.max(0, Math.floor(score)).toString().padStart(8, '0');
}

/** Build a star-row string: filled glyphs up to count, hollow for the rest. */
export function starRow(count: number, cap: number): string {
  const filled = Math.min(Math.max(0, count), cap);
  return '★'.repeat(filled) + '○'.repeat(Math.max(0, cap - filled));
}

/** Build a compact lives string (up to 5 shown; + suffix if more). */
export function livesRow(lives: number): string {
  if (lives <= 0) return '—';
  return '●'.repeat(Math.min(lives, 5)) + (lives > 5 ? '+' : '');
}

/** Compute pixel widths for the HP bar's red and green segments. */
export function hpBarWidths(
  hpFrac: number,
  greenFrac: number,
  maxWidth: number,
): { redW: number; greenW: number } {
  const redW = Math.round(Math.max(0, Math.min(1, hpFrac)) * maxWidth);
  const greenW = Math.round(Math.max(0, Math.min(1, greenFrac)) * maxWidth);
  return { redW, greenW };
}
